import { computed, inject, Injectable, signal } from '@angular/core';
import { FeedGroupChanges, FeedGroupController, FeedGroupEntity } from '@domain';
import { Observable, tap } from 'rxjs';

/** Id reservado do "grupo" dos itens sem grupo (trilho e filtro do backend). */
export const FEED_GROUP_NONE = 'none';

/** Paleta dos grupos: as cores de acento do DS, em hex porque viajam pela API. */
export const FEED_GROUP_COLORS = ['#3381ff', '#6f5cff', '#45b676', '#ff8a2a', '#e5484d', '#8a7a68'];

const COLLAPSED_STORAGE_KEY = 'rapidocs:feed-groups-collapsed';

/**
 * Estado dos GRUPOS do feed da home (`FeedGroupEntity`): a lista do usuário na
 * empresa atual e o mapa order → grupo, que é o que a lista seccionada consome.
 *
 * As mutações atualizam o signal em cima da resposta da API, sem refetch: o
 * backend devolve o grupo inteiro e o único efeito colateral (uma order sai de
 * outro grupo ao entrar neste) é reproduzido aqui.
 */
@Injectable({
    providedIn: 'root',
})
export class FeedGroupService {
    public groups = signal<FeedGroupEntity[]>([]);
    public loading = signal<boolean>(false);
    public loadedOnce = signal<boolean>(false);

    /** Grupos recolhidos na lista seccionada (só na UI; lembrado por aparelho). */
    public collapsed = signal<Set<string>>(this._readCollapsed());

    /** order id → group id, para a lista seccionar sem varrer os grupos por item. */
    public groupByOrder = computed<Map<string, string>>(() => {
        const map = new Map<string, string>();
        for (const group of this.groups()) {
            for (const orderId of group.orderIds ?? []) map.set(orderId, group._id);
        }

        return map;
    });

    // * [Injects]
    private _controller = inject(FeedGroupController);

    public load(): Observable<FeedGroupEntity[]> {
        this.loading.set(true);

        return this._controller.getAll().pipe(
            tap({
                next: groups => {
                    this.groups.set(groups ?? []);
                    this.loading.set(false);
                    this.loadedOnce.set(true);
                },
                error: () => this.loading.set(false),
            })
        );
    }

    public byId(id: string | null | undefined): FeedGroupEntity | undefined {
        return this.groups().find(group => group._id === id);
    }

    public create(name: string, color?: string): Observable<FeedGroupEntity> {
        return this._controller
            .insert(name, color)
            .pipe(tap(group => this.groups.update(groups => [...groups, group])));
    }

    public update(id: string, changes: FeedGroupChanges): Observable<FeedGroupEntity> {
        return this._controller
            .update(id, changes)
            .pipe(
                tap(group =>
                    this.groups.update(groups => groups.map(g => (g._id === id ? group : g)))
                )
            );
    }

    /** Desfaz o grupo: some do trilho e os itens dele voltam a "sem grupo". */
    public remove(id: string): Observable<void> {
        return this._controller
            .delete(id)
            .pipe(tap(() => this.groups.update(groups => groups.filter(g => g._id !== id))));
    }

    /**
     * Move uma order para um grupo (`groupId`) ou para fora de todos (`null` /
     * `'none'`). No-op se já estiver lá.
     */
    public moveItem(orderId: string, groupId: string | null): Observable<FeedGroupEntity | void> | null {
        const target = groupId === FEED_GROUP_NONE ? null : groupId;
        const current = this.groupByOrder().get(orderId) ?? null;
        if (current === target) return null;

        if (!target) {
            return this._controller.removeItem(orderId).pipe(tap(() => this._pull(orderId)));
        }

        return this._controller.addItem(target, orderId).pipe(
            tap((group: FeedGroupEntity) => {
                this._pull(orderId);
                this.groups.update(groups => groups.map(g => (g._id === group._id ? group : g)));
            })
        );
    }

    public toggleCollapsed(id: string): void {
        this.collapsed.update(set => {
            const next = new Set(set);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            this._writeCollapsed(next);

            return next;
        });
    }

    public setAllCollapsed(collapsed: boolean): void {
        const next = collapsed
            ? new Set([...this.groups().map(g => g._id), FEED_GROUP_NONE])
            : new Set<string>();
        this._writeCollapsed(next);
        this.collapsed.set(next);
    }

    /** Tira a order de qualquer grupo (local). */
    private _pull(orderId: string): void {
        this.groups.update(groups =>
            groups.map(g =>
                g.orderIds?.includes(orderId)
                    ? { ...g, orderIds: g.orderIds.filter(id => id !== orderId) }
                    : g
            )
        );
    }

    private _readCollapsed(): Set<string> {
        try {
            const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
            const ids = raw ? (JSON.parse(raw) as string[]) : [];

            return new Set(Array.isArray(ids) ? ids : []);
        } catch {
            return new Set();
        }
    }

    private _writeCollapsed(set: Set<string>): void {
        try {
            localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...set]));
        } catch {
            // Storage bloqueado: o recolhido vale só para esta sessão
        }
    }
}
